"use client";
import { useLayoutEffect, useRef } from "react";
import { useInView } from "motion/react";
import { annotate } from "rough-notation";

export function Highlighter({
  children,
  action = "highlight",
  color = "#ffd1dc",
  strokeWidth = 1.5,
  animationDuration = 600,
  iterations = 2,
  padding = 2,
  multiline = true,
  isView = false,
  delay = 0,
}) {
  const elementRef = useRef(null);

  const isInView = useInView(elementRef, {
    once: true,
    margin: "-10%",
  });

  // If isView is false, always show. If isView is true, wait for inView
  const shouldShow = !isView || isInView;

  useLayoutEffect(() => {
    const element = elementRef.current;
    if (!shouldShow || !element) return;

    let timer;

    const annotation = annotate(element, {
      type: action,
      color,
      strokeWidth,
      animationDuration,
      iterations,
      padding,
      multiline,
    });

    timer = setTimeout(() => {
      annotation.show();
    }, delay);

    const resizeObserver = new ResizeObserver(() => {
      annotation.hide();

      clearTimeout(timer);
      timer = setTimeout(() => {
        annotation.show();
      }, delay);
    });

    resizeObserver.observe(element);

    return () => {
      clearTimeout(timer);
      resizeObserver.disconnect();
      annotation.remove();
    };
  }, [
    shouldShow,
    action,
    color,
    strokeWidth,
    animationDuration,
    iterations,
    padding,
    multiline,
    delay,
  ]);

  return (
    <span
      ref={elementRef}
      className="relative inline-block bg-transparent -translate-y-2 px-2 py-1 dark:text-black"
    >
      {children}
    </span>
  );
}
